// The person builds their own day here.
//
// Clicking a set adds it. Clicking it again removes it. That is the whole
// interaction, and nothing else on the screen moves when they do it: no
// re-solve, no set traded away, no plan rewritten underneath them. Two picks
// that overlap stay picked, because a human can see they overlap and may want
// both anyway.
//
// Where picks share minutes, or the gap between them is shorter than the walk,
// the day list says so and offers ways through it. Those are buttons. Nothing
// is applied until one is pressed.
//
// The one exception is the first draft, and it is explicit: the person asks for
// it, it is built once, and it replaces picks only after they confirm it. After
// that the plan is theirs again and nothing re-solves it.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { DraftOutcome } from '../../data/store.tsx';
import { ShareDay } from '../components/ShareDay.tsx';
import { PlanLink } from '../components/PlanLink.tsx';
import { ImprovePlan } from '../components/ImprovePlan.tsx';
import { LineupChangeNotice } from '../components/LineupChangeNotice.tsx';
import { useApp } from '../../data/store.tsx';
import { GridLegend, StageGrid } from '../components/StageGrid.tsx';
import { ScheduleAgenda } from '../components/ScheduleAgenda.tsx';
import { ScheduleTimeline } from '../components/ScheduleTimeline.tsx';
import { ScheduleHeader } from '../components/ScheduleHeader.tsx';
import type { AgendaBlock } from '../components/ScheduleAgenda.tsx';
import { optionsForClash } from '../../engine/picks.ts';
import { minutesOfMusic, walkingMinutes } from '../../engine/picks.ts';
import type { Clash } from '../../engine/picks.ts';
import { fmtRange, fmtTime } from '../../engine/time.ts';
import type { PlanBlock } from '../../engine/types.ts';
import { dayColor, readSettings, stageColor, stageColor2 } from '../theme.ts';
import type { CSSProperties } from 'react';
import '../../styles/planning-refinements.css';

const VIEW_KEY = 'festival-schedule-view';

export function Schedule() {
  const { state, actions, derived } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [openClash, setOpenClash] = useState<string | null>(null);
  const location = useLocation();
  const [draft, setDraft] = useState<DraftOutcome | null>(
    () => (location.state as { draft?: DraftOutcome } | null)?.draft ?? null,
  );
  const [drafting, setDrafting] = useState(false);
  // A draft built on the preferences screen arrives here scrolled to wherever
  // that screen was; the result line is at the top.
  useEffect(() => {
    if ((location.state as { draft?: DraftOutcome } | null)?.draft) window.scrollTo(0, 0);
  }, [location.key]);
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [draftError, setDraftError] = useState('');

  const runDraft = async (replace: boolean) => {
    setDrafting(true);
    setDraftError('');
    try {
      setDraft(await actions.buildFirstDraft({ replace }));
      setConfirmRebuild(false);
    } catch {
      setDraftError('The draft could not be built. Your picks were not changed.');
    } finally {
      setDrafting(false);
    }
  };

  // The day in view lives in the address rather than in this component, so a day
  // can be linked to, shared, and returned to by the back button.
  const askedDay = derived.dayContexts.findIndex((c) => c.day.id === params.get('day'));
  const dayIndex = askedDay >= 0 ? askedDay : 0;
  const setDayIndex = (index: number) => {
    const id = derived.dayContexts[index]?.day.id;
    if (!id) return;
    const next = new URLSearchParams(params);
    next.set('day', id);
    setParams(next, { replace: true });
  };

  const [view, setView] = useState<'agenda' | 'grid' | 'timeline'>(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY);
      if (saved === 'agenda' || saved === 'grid' || saved === 'timeline') return saved;
    } catch {
      // A browser refusing storage falls through to the settings answer.
    }
    if (readSettings().layout === 'list') return 'agenda';
    return typeof window !== 'undefined' && window.innerWidth < 900 ? 'agenda' : 'grid';
  });

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Not remembering a view is not a reason to fail the screen.
    }
  }, [view]);

  const ctx = derived.dayContexts[dayIndex];
  if (!ctx) return <p className="muted">No festival days are loaded.</p>;

  const picks = derived.picksByDay.get(ctx.day.id) ?? [];
  const clashes = derived.clashesByDay.get(ctx.day.id) ?? [];
  const walk = state.walk ?? { festivalId: '', minutes: {}, defaultMinutes: 8 };

  const nameOfSet = (setId: string) => {
    const set = derived.setById.get(setId);
    return (set && derived.artistById.get(set.artistId)?.name) ?? 'that set';
  };

  // The grid highlights exactly what is picked, with a partial mark where the
  // person set their own arrive or leave time.
  const blocks: PlanBlock[] = picks.map((p) => ({
    setId: p.setId,
    stageId: p.set.stageId,
    startTs: p.startTs,
    endTs: p.endTs,
    mode: p.arrivesLate ? 'catch_close' : p.leavesEarly ? 'catch_open' : 'full',
    locked: false,
    utility: 0,
  }));

  const agenda: AgendaBlock[] = picks.map((p) => ({
    id: p.setId,
    label:
      nameOfSet(p.setId) +
      (p.arrivesLate || p.leavesEarly ? ` (${fmtTime(p.startTs)} to ${fmtTime(p.endTs)})` : ''),
    place: derived.stageById.get(p.set.stageId)?.name ?? 'that stage',
    startTs: p.startTs,
    endTs: p.endTs,
    kind: 'set',
    locked: false,
    onToggleLock: undefined,
    color: stageColor(p.set.stageId),
    color2: stageColor2(p.set.stageId),
  }));

  const music = minutesOfMusic(picks);
  const walking = walkingMinutes(picks, walk);

  const summary =
    picks.length === 0
      ? `Choose sets for ${ctx.day.label} to start your plan.`
      : `${picks.length} ${picks.length === 1 ? 'set' : 'sets'}, ${Math.floor(music / 60)}h ${music % 60}m of music, ${walking} min of walking.`;

  const emptyPlan = derived.pickedSetIds.size === 0;
  const profile = state.profiles.find((p) => p.userId === state.userId);
  const hasData =
    Object.keys(profile?.subgenreWeights ?? {}).length > 0 ||
    state.ratings.some((r) => r.userId === state.userId && r.source === 'manual') ||
    derived.myMustSee.length > 0;

  return (
    <div className="cols schedpage planning-page">
      <div>
        <LineupChangeNotice />
        {emptyPlan && (
          <section className="panel firstdraft">
            <h2>Build a first-pass schedule</h2>
            <p className="muted">
              Give the planner a little context, or start with what you've already saved. You can
              change every choice later.
            </p>
            <div className="flex wrap">
              <button
                className="primary"
                type="button"
                disabled={drafting}
                onClick={() => (hasData ? void runDraft(false) : navigate('/preferences?quick=1&returnTo=%2Fplan'))}
              >
                {drafting ? 'Building…' : hasData ? 'Build from my saved data' : 'Quick setup'}
              </button>
              <button type="button" className="ghost" onClick={() => navigate('/preferences?returnTo=%2Fplan')}>
                {hasData ? 'Tune my preferences' : 'Set preferences in detail'}
              </button>
            </div>
            <button type="button" className="textlink" onClick={() => setView('timeline')}>
              Browse sets
            </button>
          </section>
        )}

        {draftError && <p role="alert" className="planning-error">{draftError}</p>}
        {draft?.built && (
          <p className="draft-result" role="status">
            Drafted {draft.setCount} {draft.setCount === 1 ? 'set' : 'sets'} across {draft.dayCount}{' '}
            {draft.dayCount === 1 ? 'day' : 'days'}. Tap any set to change it.
            {draft.infeasible.map((d) => (
              <span key={d.dayId} className="draft-infeasible">
                {derived.dayContexts.find((c) => c.day.id === d.dayId)?.day.label ?? 'One day'}: {d.reason}
              </span>
            ))}
          </p>
        )}
        {!emptyPlan && (
          <div className="draft-rebuild">
            {confirmRebuild ? (
              <>
                <span>
                  This replaces all {derived.pickedSetIds.size}{' '}
                  {derived.pickedSetIds.size === 1 ? 'pick' : 'picks'} with a new draft.
                </span>
                <button type="button" className="small" disabled={drafting} onClick={() => void runDraft(true)}>
                  {drafting ? 'Building…' : 'Replace my picks'}
                </button>
                <button type="button" className="small ghost" onClick={() => setConfirmRebuild(false)}>
                  Keep my picks
                </button>
              </>
            ) : (
              <button type="button" className="textlink" onClick={() => setConfirmRebuild(true)}>
                Rebuild draft
              </button>
            )}
          </div>
        )}

        <ScheduleHeader
          dayId={ctx.day.id}
          dayLabel={ctx.day.label}
          badge="My plan"
          lede={summary}
          right={
            <div className="viewtoggle" role="group" aria-label="Schedule view">
              <button type="button" aria-pressed={view === 'agenda'} onClick={() => setView('agenda')}>
                List
              </button>
              <button type="button" aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
                Grid
              </button>
              <button type="button" aria-pressed={view === 'timeline'} onClick={() => setView('timeline')}>
                Timeline
              </button>
            </div>
          }
        />

        {view === 'agenda' ? (
          picks.length === 0 ? (
            <div className="panel planning-empty">
              <p>No sets saved for {ctx.day.label}.</p>
              <button type="button" onClick={() => setView('timeline')}>Choose sets for {ctx.day.label}</button>
            </div>
          ) : (
            <ScheduleAgenda blocks={agenda} />
          )
        ) : view === 'timeline' ? (
          <ScheduleTimeline
            ctx={ctx}
            blocks={blocks}
            artistById={derived.artistById}
            pickedSetIds={derived.pickedSetIds}
            onSetClick={(setId) => void actions.togglePick(setId)}
          />
        ) : (
          <>
            <StageGrid
              ctx={ctx}
              blocks={blocks}
              artistById={derived.artistById}
              pickedSetIds={derived.pickedSetIds}
              onSetClick={(setId) => void actions.togglePick(setId)}
            />
            <GridLegend />
          </>
        )}

        {/* Under the chart, where the eye already is after reading a day. */}
        <div className="daytabs under" role="group" aria-label="Festival day">
          {derived.dayContexts.map((c, i) => (
            <button
              key={c.day.id}
              type="button"
              aria-pressed={i === dayIndex}
              className={i === dayIndex ? 'active' : ''}
              style={{ '--day': dayColor(c.day.id) } as CSSProperties}
              onClick={() => setDayIndex(i)}
            >
              {c.day.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {/* The ranked queue covers clashes along with everything else worth
            asking. The per-day clash cards below stay for the day in view. */}
        <ImprovePlan compact />
        <PlanLink />
        <ShareDay key={ctx.day.id} day={ctx.day} />
        {clashes.length > 0 && (
          <div className="panel" id="clashes">
            <h3>Clashes on {ctx.day.label}</h3>
            {clashes.map((c) => (
              <ClashCard
                key={c.id}
                clash={c}
                nameOf={nameOfSet}
                open={openClash === c.id}
                onToggle={() => setOpenClash(openClash === c.id ? null : c.id)}
                onChoose={(spans, drops) => {
                  void actions.applyClashOption(spans, drops);
                  setOpenClash(null);
                }}
              />
            ))}
          </div>
        )}

        <div className="panel">
          <div className="panelhead">
            <h3>Your picks</h3>
            {picks.length > 0 && (
              <button className="small ghost" aria-label={`Clear all picks for ${ctx.day.label}`} onClick={() => void actions.clearPicks(ctx.day.id)}>
                Clear day
              </button>
            )}
          </div>
          {picks.length === 0 ? (
            <button type="button" className="small ghost" onClick={() => setView('timeline')}>Browse sets</button>
          ) : (
            <div className="rowlist">
              {picks.map((p) => (
                <div key={p.setId} className="row">
                  <span>
                    {nameOfSet(p.setId)}
                    <span className="sub" style={{ display: 'block' }}>
                      {derived.stageById.get(p.set.stageId)?.name} {fmtRange(p.startTs, p.endTs)}
                      {p.arrivesLate ? ' · joining late' : ''}
                      {p.leavesEarly ? ' · leaving early' : ''}
                    </span>
                  </span>
                  <span className="flex planning-actions">
                    {(p.arrivesLate || p.leavesEarly) && (
                      <button
                        className="small ghost"
                        aria-label={`Restore the whole set for ${nameOfSet(p.setId)}`}
                        onClick={() => void actions.setPickSpan(p.setId, undefined, undefined)}
                      >
                        Whole set
                      </button>
                    )}
                    <button className="small ghost" aria-label={`Remove ${nameOfSet(p.setId)} from my plan`} onClick={() => void actions.togglePick(p.setId)}>
                      Remove
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClashCard({
  clash,
  nameOf,
  open,
  onToggle,
  onChoose,
}: {
  clash: Clash;
  nameOf: (setId: string) => string;
  open: boolean;
  onToggle: () => void;
  onChoose: (spans: { setId: string; startTs: number; endTs: number }[], drops: string[]) => void;
}) {
  const first = nameOf(clash.first.setId);
  const second = nameOf(clash.second.setId);

  // A statement of the arithmetic. What to do about it is the reader's call.
  const line =
    clash.kind === 'overlap'
      ? `${first} and ${second} overlap by ${clash.overlapMinutes} min.`
      : `${clash.gapMinutes} min between ${first} and ${second}, and the walk is about ${clash.walkMinutes}.`;

  const options = open ? optionsForClash(clash, nameOf, fmtTime) : [];

  return (
    <div style={{ marginBottom: 10 }}>
      <p style={{ margin: '0 0 6px', fontSize: 16 }}>{line}</p>
      <button className="small ghost" aria-expanded={open} onClick={onToggle}>
        {open ? 'Hide options' : 'Compare options'}
      </button>
      {open && (
        <div className="rowlist" style={{ marginTop: 8 }}>
          {options.map((o) => (
            <div key={o.id} className="row">
              <span style={{ fontSize: 14 }}>
                {o.label}
                <span className="sub" style={{ display: 'block' }}>
                  {o.minutesKept.map((k) => `${nameOf(k.setId)} ${k.minutes} min`).join(' · ')}
                </span>
              </span>
              <button className="small" onClick={() => onChoose(o.spans, o.drops)}>
                Use this
              </button>
            </div>
          ))}
          <p className="muted" style={{ fontSize: 16, margin: '6px 0 0' }}>
            Your current picks stay saved until you choose an option.
          </p>
        </div>
      )}
    </div>
  );
}
